import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAdminAuthStore } from "@charisol-spark/client-core";
import { Spinner } from "@/components/ui/Spinner";

/**
 * Route guard that redirects a signed-out admin to /login, preserving the
 * intended destination in location state.
 *
 * Trimmed from packages/frontend/src/components/auth/ProtectedRoute.tsx:
 * no post-login splash screen (admin has no onboarding hero to preload, and
 * the console is small enough that a brief loading flash is not worth the
 * extra state machine) — just the session-resume wait for the case
 * useAuthInitialization is still exchanging a refresh token for a fresh
 * access token (see that hook for why this can happen on a perfectly good
 * session).
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const user = useAdminAuthStore((s) => s.user);
  const location = useLocation();

  const hasToken = !!sessionStorage.getItem("spark_access_token");

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!hasToken) {
    const hasRefreshToken = !!localStorage.getItem("spark_refresh_token");
    if (hasRefreshToken) {
      return (
        <div className="flex min-h-screen items-center justify-center">
          <Spinner size="lg" />
        </div>
      );
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
